import { fetchApi } from '@/services/api-client';

/** How much attention a category asks for; drives the badge colour and nothing else. */
export type GrowthSeverity = 'typical' | 'monitor' | 'attention';

/**
 * One WHO indicator for one measurement. `category` is a translation key ("bmi.healthy-weight"),
 * never display text — the API leaves the wording to whoever is reading, in their language.
 */
export type GrowthIndicator = {
  percentile: number | null;
  z: number;
  category: string;
  severity: GrowthSeverity;
};

export type Measurement = {
  id: string;
  studentId: string;
  measuredAt: string;
  weightKg: number;
  heightCm: number;
  bmi: number;
  /** Null when the child has no date of birth or sex on file, or WHO does not define it at that age. */
  heightForAge: GrowthIndicator | null;
  bmiForAge: GrowthIndicator | null;
  /** WHO stops defining weight-for-age at ten years old, so this is null for older children. */
  weightForAge: GrowthIndicator | null;
  createdAt: string;
};

export type MeasurementInput = { measuredAt: string; weightKg: number; heightCm: number };

/** One month of WHO's reference, as values in the indicator's own unit. */
export type GrowthBand = { ageMonths: number; p3: number; p15: number; p50: number; p85: number; p97: number };

export type GrowthChartSeries = {
  indicator: 'height-for-age' | 'bmi-for-age' | 'weight-for-age';
  unit: 'cm' | 'kg' | 'kg/m2';
  /** False where WHO publishes no curve across this window — weight-for-age past ten, above all. */
  defined: boolean;
  reference: GrowthBand[];
  points: { ageMonths: number; value: number; measuredAt: string }[];
};

export type GrowthChart = {
  /** False when the child has no date of birth or sex on file: no age, so no comparison. */
  comparable: boolean;
  fromMonths: number;
  toMonths: number;
  series: GrowthChartSeries[];
};

export const medicalMeasurementsApi = {
  list: (studentId: string) => fetchApi<Measurement[]>(`/students/${studentId}/measurements`),
  chart: (studentId: string) => fetchApi<GrowthChart>(`/students/${studentId}/growth-chart`),
  create: (studentId: string, data: MeasurementInput) =>
    fetchApi<Measurement>(`/students/${studentId}/measurements`, { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: MeasurementInput) =>
    fetchApi<Measurement>(`/medical/measurements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string) => fetchApi<{ success: true }>(`/medical/measurements/${id}`, { method: 'DELETE' }),
};
